import {ThunkDispatch, UnknownAction} from '@reduxjs/toolkit';
import {Mutex} from 'async-mutex';
import {resetFeatureFlags} from 'feature-flags';

import {isNotNil} from 'ramda';

import {browserStorageKey} from '@dms/config';
import {getWorkspaceFromUri, redirectToLogin, redirectToWorkspace} from '@dms/routes';

import {publicApi} from '../modules/publicApi/publicApi';
import {RefreshTokenApiArg} from '../modules/publicApi/types';

type RefreshDispatch = ThunkDispatch<unknown, unknown, UnknownAction>;
type GetState = () => unknown;

// Bound the refresh round-trip: a hung auth endpoint must not hold the mutex
// indefinitely, which would freeze every caller waiting behind it.
const REFRESH_TIMEOUT_MS = 15_000;

/**
 * Module-level mutex — the single point that serializes token refreshes across every
 * caller in this realm. It is intentionally a singleton; importing this file from multiple places
 * shares the same lock.
 */
const mutex = new Mutex();

/**
 * Single-flight access-token refresh shared by every caller that can hit a 401
 * (REST base queries, the axios interceptor, the AppSync WebSocket).
 *
 * All redirect/logout side-effects live here so the three former copies cannot drift.
 */
export const refreshAccessToken = async (dispatch: RefreshDispatch, getState: GetState) => {
  if (typeof window === 'undefined') {
    return false;
  }

  const tokenBeforeLock = sessionStorage.getItem(browserStorageKey.ACCESS_TOKEN);

  return await mutex.runExclusive(() => {
    const currentToken = sessionStorage.getItem(browserStorageKey.ACCESS_TOKEN);
    if (isNotNil(currentToken) && currentToken !== tokenBeforeLock) {
      // A concurrent caller already refreshed while we waited — reuse its token.
      return true;
    }

    const refreshToken = localStorage.getItem(browserStorageKey.REFRESH_TOKEN);
    const {workspace, shouldRedirectToAppWorkspace} = getWorkspaceFromUri();
    const currentUserEmail = getCurrentUserEmail(getState());

    if (!workspace || shouldRedirectToAppWorkspace) {
      redirectToWorkspace();
      return false;
    }

    if (!refreshToken) {
      redirectToLogin({currentUserEmail});
      return false;
    }

    const refreshTokenArgs: RefreshTokenApiArg = {
      workspace,
      refreshTokenRequestBody: {refreshToken},
    };

    return withTimeout(
      dispatch(publicApi.endpoints.refreshToken.initiate(refreshTokenArgs)).unwrap(),
      REFRESH_TIMEOUT_MS
    )
      .then(({token, refreshToken: nextRefreshToken}) => {
        sessionStorage.setItem(browserStorageKey.ACCESS_TOKEN, token);
        localStorage.setItem(browserStorageKey.REFRESH_TOKEN, nextRefreshToken);

        return true;
      })
      .catch(() => {
        sessionStorage.removeItem(browserStorageKey.ACCESS_TOKEN);
        localStorage.removeItem(browserStorageKey.REFRESH_TOKEN);
        resetFeatureFlags();
        redirectToLogin({currentUserEmail});

        return false;
      });
  });
};

/**
 * Reject if `promise` has not settled within `timeoutMs` so a stuck endpoint
 * degrades to a clean re-login instead of an indefinitely held lock.
 */
const withTimeout = <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('Token refresh timed out')), timeoutMs);
  });

  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

const getCurrentUserEmail = (state: unknown) => {
  if (
    isNotNil(state) &&
    typeof state === 'object' &&
    'user' in state &&
    isNotNil(state.user) &&
    typeof state.user === 'object' &&
    'email' in state.user &&
    typeof state.user.email === 'string'
  ) {
    return state.user.email;
  }

  return undefined;
};
